import * as React from "react";
import { cn } from "@/shared/lib/cn";
import { useIsMobile } from "@/shared/lib/hooks/useIsMobile";
import { TooltipProvider } from "@/shared/ui/kit/tooltip";

const SIDEBAR_WIDTH = "16rem";
export const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3.5rem";

export type SidebarCssVars = React.CSSProperties & Partial<Record<"--sidebar-width" | "--sidebar-width-icon", string>>;

type SidebarContextValue = {
    state: "expanded" | "collapsed";
    open: boolean;
    setOpen: (open: boolean) => void;
    openMobile: boolean;
    setOpenMobile: (open: boolean) => void;
    isMobile: boolean;
    toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextValue | null>(null);

export function useSidebar(): SidebarContextValue {
    const context = React.useContext(SidebarContext);
    if (!context) {
        throw new Error("useSidebar must be used within a SidebarProvider.");
    }
    return context;
}

interface SidebarProviderProps extends React.HTMLAttributes<HTMLDivElement> {
    defaultOpen?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    style?: SidebarCssVars;
}

export const SidebarProvider = React.forwardRef<HTMLDivElement, SidebarProviderProps>(
    ({ defaultOpen = true, open: openProp, onOpenChange, className, style, children, ...props }, ref) => {
        const isMobile = useIsMobile();
        const [openMobile, setOpenMobile] = React.useState(false);
        const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
        const open = openProp ?? uncontrolledOpen;

        const setOpen = React.useCallback(
            (value: boolean) => {
                if (onOpenChange) onOpenChange(value);
                else setUncontrolledOpen(value);
            },
            [onOpenChange]
        );

        const toggleSidebar = React.useCallback(() => {
            if (isMobile) setOpenMobile((current) => !current);
            else setOpen(!open);
        }, [isMobile, open, setOpen]);

        React.useEffect(() => {
            if (!isMobile) setOpenMobile(false);
        }, [isMobile]);

        const value = React.useMemo<SidebarContextValue>(
            () => ({
                state: open ? "expanded" : "collapsed",
                open,
                setOpen,
                openMobile,
                setOpenMobile,
                isMobile,
                toggleSidebar,
            }),
            [open, setOpen, openMobile, isMobile, toggleSidebar]
        );

        const cssVars: SidebarCssVars = {
            "--sidebar-width": SIDEBAR_WIDTH,
            "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
            ...style,
        };

        return (
            <SidebarContext.Provider value={value}>
                <TooltipProvider delayDuration={0}>
                    <div ref={ref} style={cssVars} className={cn("group/sidebar-wrapper flex w-full", className)} {...props}>
                        {children}
                    </div>
                </TooltipProvider>
            </SidebarContext.Provider>
        );
    }
);
SidebarProvider.displayName = "SidebarProvider";
